import { PureComponent } from "react";

import { HeaderBar, TriggerButton } from "widgets";

import { type Dictionary } from "../../app-utils/type.js";
import { type ColumnAnnotation, type Dataset, type DatasetColumn, type StringMapEntry } from "../../graphql.js";
import { type IColumnAnnotationFilter, type IColumnAnnotationFilterMap } from "../../redux/modules/dataset-viewer.js";
import { type IFilterChartData, type IFilterChartValue } from "./FilterChart.js";
import FilterPanelItem from "./FilterPanelItem.js";
import { ANNOTATION_TYPE, HISTOGRAM_LINE_LIMIT } from "@/settings/settings.js";

export interface IFilterPanelProps {
  dataset: Dataset;
  filteredOutAnnotations: IColumnAnnotationFilterMap;
  filteredOutColumns: string[];
  filteredOutValueIndicesByColumn: Dictionary<string[]>;
  availableAnnotations: ColumnAnnotation[];
  selectedAnnotations: ColumnAnnotation[];

  closeFilterPanel: () => void;
  onFilteredConditionsChanged: (conditionField: string, conditionFilter: IColumnAnnotationFilter) => void;
  onShowTooltip?: (text: string, target: any) => void;
  onHideTooltip?: () => void;
}

interface IFilterPanelState {
  allHistogramData: Record<string, IFilterChartData>; // map by columnAnnotationKey
}
/**
 * Component representing the list of conditions/annotations to be filtered in the context of the Analytical Data Viewer.
 *
 * created on 4/27/2017
 */
class FilterPanel extends PureComponent<IFilterPanelProps, IFilterPanelState> {
  constructor(props: IFilterPanelProps) {
    super(props);
    this.state = {
      allHistogramData: {},
    };
  }

  sortAnnotationsByNumberOfDistinctValues = (conditionA: ColumnAnnotation, conditionB: ColumnAnnotation) => {
    return conditionB.valueSet.length - conditionA.valueSet.length;
  };

  componentDidMount() {
    this.calculateStandardHistogramData();
  }

  componentDidUpdate(prevProps: Readonly<IFilterPanelProps>): void {
    if (
      this.props.dataset !== prevProps.dataset ||
      this.props.filteredOutAnnotations !== prevProps.filteredOutAnnotations ||
      this.props.filteredOutColumns !== prevProps.filteredOutColumns ||
      this.props.filteredOutValueIndicesByColumn !== prevProps.filteredOutValueIndicesByColumn
    ) {
      this.calculateStandardHistogramData();
    }
  }

  private calculateSampleHistogramData = (condition: ColumnAnnotation): IFilterChartData => {
    const histogramData: IFilterChartData = {
      type: null,
      valueSet: [],
    };

    histogramData.type =
      condition.valueSet.length > HISTOGRAM_LINE_LIMIT ? ANNOTATION_TYPE.NUMERICAL : ANNOTATION_TYPE.CATEGORICAL;

    this.props.dataset.columns.forEach((column: DatasetColumn) => {
      histogramData.valueSet.push({
        name: column.name,
        totalCount: 1,
        remainingCount: this.props.filteredOutColumns.includes(column.field) ? 0 : 1,
      });
    });
    return histogramData;
  };

  calculateStandardHistogramData = (): void => {
    const histogramData: Record<string, IFilterChartData> = {}; // map by columnAnnotationKey

    if (!this.props.dataset) {
      this.setState({ allHistogramData: {} });
      return;
    }

    // get total statistics based on the dataset columns
    this.props.dataset.columns.forEach((column: DatasetColumn) => {
      column.annotations.forEach((columnAnnotation: StringMapEntry) => {
        if (!histogramData[columnAnnotation.key]) {
          // create a new hash entry -
          // we need to retrieve the type from the dataset.columnAnnotations because our column.annotations are only map entries
          const myType: string = this.props.dataset.columnAnnotations.find((fullColumnAnnotation: ColumnAnnotation) => {
            return fullColumnAnnotation.field === columnAnnotation.key;
          }).type;

          histogramData[columnAnnotation.key] = { type: myType, valueSet: [] };
        }

        // check if we already have such a value, otherwise create a new one
        let filterChartValue: IFilterChartValue = histogramData[columnAnnotation.key].valueSet.find((value) => {
          return value.name === columnAnnotation.value;
        });
        if (!filterChartValue) {
          filterChartValue = { name: columnAnnotation.value, totalCount: 0, remainingCount: 0 };
          histogramData[columnAnnotation.key].valueSet.push(filterChartValue);
        }

        filterChartValue.totalCount += 1;

        // get remaining counts depending on the filters
        if (
          !this.props.filteredOutColumns.includes(column.field) &&
          !this.props.filteredOutValueIndicesByColumn[column.field]?.includes(columnAnnotation.multiValueIndex)
        ) {
          filterChartValue.remainingCount += 1;
        }

        // if the number of valueSets exceeds a limit, set the valueType to numerical in order to enforce a line histogram
        if (histogramData[columnAnnotation.key].valueSet.length > HISTOGRAM_LINE_LIMIT) {
          histogramData[columnAnnotation.key].type = ANNOTATION_TYPE.NUMERICAL;
        }
      });
    });

    this.setState({ allHistogramData: histogramData });
  };

  getHistogramData = (condition: ColumnAnnotation): IFilterChartData => {
    let histogramData: IFilterChartData;

    const annotationKey: string = condition.field;

    // special handling of the "samples" condition
    if (annotationKey === "columns") {
      histogramData = this.calculateSampleHistogramData(condition);
    } else {
      // get-pre-calculated data
      histogramData = this.state.allHistogramData[annotationKey];
      if (histogramData) {
        // note that the valueSet in the histogramData retrieved from the pre-calculated data structure
        // does not know about the ordering in the availableAnnotations. We must apply it manually!
        const sortedValueSet = [];
        condition.valueSet.forEach((condValue) => {
          const hitValue = histogramData?.valueSet.find((histValue) => histValue.name === condValue);
          if (hitValue) {
            sortedValueSet.push(hitValue);
          }
        });

        // do not modify the object in allHistogramData, but assign anonymous object!
        histogramData = { type: histogramData.type, valueSet: sortedValueSet };
      }
    }
    return histogramData;
  };

  render() {
    if (!this.props.dataset) {
      return null;
    }

    const sortedAnnotations: ColumnAnnotation[] = this.props.availableAnnotations
      .map(
        (condition: ColumnAnnotation) =>
          // in case the condition is one of the selected, use the selected one (may contain a modified value set order)
          this.props.selectedAnnotations.find((c: ColumnAnnotation) => c.field === condition.field) || condition,
      )
      .sort(this.sortAnnotationsByNumberOfDistinctValues);

    const columnsCount: number = this.props.dataset.columns.length;
    const filteredInColumnsCount: number = columnsCount - this.props.filteredOutColumns.length;

    return (
      <>
        <HeaderBar
          title={"Filters"}
          subtitle={`${filteredInColumnsCount} of ${columnsCount} samples`}
          actions={[
            <TriggerButton
              icon={"close"}
              label={"Close"}
              onPress={this.props.closeFilterPanel}
              key={"filter-panel-action-1"}
            />,
          ]}
        />
        <div className="chart-filters-scroller">
          <div className="chart-filters-items">
            {sortedAnnotations.map((condition: ColumnAnnotation) => (
              <FilterPanelItem
                condition={condition}
                key={condition.field}
                filteredConditions={this.props.filteredOutAnnotations}
                filteredConditionsChanged={this.props.onFilteredConditionsChanged}
                getHistogramData={this.getHistogramData}
              />
            ))}
          </div>
        </div>
      </>
    );
  }
}

export default FilterPanel;
